import React, { useRef, useState, useEffect, useMemo } from 'react'
import {
	Toolbar,
	makeStyles,
	Typography,
	useTheme,
	Link,
	Icon,
	MenuItem,
	IconButton,
	CircularProgress,
	Tooltip
} from '@material-ui/core'
import { AddOutlined } from '@material-ui/icons'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { table } from '../../../services/theme'
import moment from 'moment-timezone'
import { MainButton } from '../../../components'
import TrainingClassDialog from '../../../components/Dialogs/Training/TrainingClassDialog'
import {
	DIALOG_EDIT_MODES,
	TRAINING_CLASS_TEMPLATE,
	convert24HourTimeTo12HourTime,
	ADMIN_ONLY,
	TRAINING_CLASS,
	FACILITY_VISIT_RELATED_TRAINING,
	STATUS_ID
} from '../../../utils'
import { getChipStyles } from '../../../services/helpers'
import { Link as RouterLink } from 'react-router-dom'
import { dangers, gray, purple, red, yellow } from '../../../utils/colors'
import StyledToolbarDatePicker from '../../../components/Common/StyledToolbarDatePicker'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import useDomDimensions from '../../../hooks/useDomDimensions'
import clsx from 'clsx'
import StyledSimpleSelect from '../../../components/Common/StyledSimpleSelect'
import StyledTextField from '../../../components/Common/StyledTextField'
import StyledKeyboardTimePicker from '../../../components/Common/StyledKeyboardTimePicker'
import useQuery from '../../../hooks/useQuery'

// Using this to handle sizing the calendar view based on dom dimensions
// Since the calendar element needs a parent with a fixed height
const TOOLBAR_HEIGHT = 85

const localizer = momentLocalizer(moment)

const VIEW_LABELS = { month: 'Month View', week: 'Week View' }

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1
	},
	toolbar: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		paddingLeft: '1.5rem !important',
		paddingRight: '1.5rem !important',
		height: '5.3125rem',
		gap: '1.5rem'
	},
	toolbarHeader: {
		fontSize: '1.375rem',
		fontWeight: 800,
		lineHeight: '30px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	},
	weekViewEvent: {
		display: 'flex',
		padding: '.5rem',
		border: 'none',
		backgroundColor: purple[50],
		color: gray[900],
		fontSize: '0.875rem',
		fontFamily: 'Nunito',
		fontWeight: 500,
		'& > div': { width: '12.5rem' },
		borderRadius: '.375rem'
	},
	weekViewEventFVRT: {
		backgroundColor: gray[400]
	},
	weekViewEventPrivate: {
		backgroundColor: yellow[300]
	},
	weekViewEventDot: {
		width: '0.4375rem',
		height: '0.4375rem',
		borderRadius: '50%',
		backgroundColor: purple[600],
		display: 'inline-block',
		position: 'relative',
		marginRight: '.5rem',
		top: '.375rem'
	},
	weekViewEventDotPrivate: {
		backgroundColor: yellow[600]
	},
	weekViewEventDotFVRT: {
		backgroundColor: gray[700]
	},
	weekViewEventLock: {
		fontSize: '.55rem',
		overflow: 'unset',
		marginRight: '.45rem',
		position: 'relative',
		top: '-1px',
		color: purple[600]
	},
	weekViewEventIssues: {
		fontSize: '.95rem',
		overflow: 'unset',
		marginRight: '.45rem',
		position: 'relative',
		top: '-1px',
		color: dangers[500]
	},
	dayViewEventContainer: {
		padding: '0 .5rem',
		flex: 1,
		overflow: 'hidden',
		fontSize: '0.875rem',
		fontFamily: 'Nunito',
		fontWeight: 500
	},
	dayViewEventDot: {
		width: '0.4375rem',
		height: '0.4375rem',
		borderRadius: '50%',
		backgroundColor: purple[600],
		display: 'inline-block',
		position: 'relative',
		top: '-0.0625rem',
		marginRight: '.3rem'
	},
	dayViewEventDotFVRT: {
		backgroundColor: gray[700]
	},
	dayViewEventDotPrivate: {
		backgroundColor: yellow[600]
	},
	dayEventLock: {
		fontSize: '.6rem',
		overflow: 'unset',
		marginRight: '.3rem',
		position: 'relative',
		top: '-1px',
		color: purple[600]
	},
	dayEventIssues: {
		fontSize: '.6rem',
		overflow: 'unset',
		marginRight: '.3rem',
		position: 'relative',
		top: '-1px',
		color: dangers[500]
	},
	dayViewEvent: {
		backgroundColor: purple[50],
		padding: '0.1875rem 0.375rem',
		borderRadius: '.25rem',
		flexDirection: 'row',
		overflow: 'hidden'
	},
	dayViewEventFVRT: {
		backgroundColor: gray[400]
	},
	dayViewEventPrivate: {
		backgroundColor: yellow[300]
	},
	dayViewEventInnerContainer: {
		overflow: 'hidden',
		width: '100%',
		alignItems: 'center',
		gap: '.3rem'
	},
	dayViewEventTextContainer: { textWrap: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' },
	eventSelected: {
		backgroundColor: purple[50],
		border: `0.125rem solid ${purple[600]}`,
		zIndex: 10000
	},
	eventSelectedPrivate: {
		backgroundColor: yellow[300],
		border: `0.125rem solid ${yellow[600]}`
	},
	eventSelectedFVRT: {
		backgroundColor: gray[400],
		border: `0.125rem solid ${gray[700]}`
	},
	classCard: {
		display: 'flex',
		flexDirection: 'column',
		padding: '1rem',
		border: `0.0625rem solid ${gray[500]}`,
		borderRadius: '0.375rem',
		gap: '.375rem'
	},
	classCardNameContainer: {
		display: 'flex',
		flexDirection: 'row',
		gap: '.5rem',
		alignItems: 'center'
	},
	classCardDetailsContainer: {
		paddingLeft: '2rem',
		fontSize: '0.875rem',
		fontWeight: 500,
		fontFamily: 'Nunito'
	},
	fvrtCardDetailsOuterContainer: { display: 'flex', flexDirection: 'row', gap: '2rem' },
	fvrtCardDetailsInnerContainer: { display: 'flex', flexDirection: 'column', gap: '.375rem' },
	classCardDot: {
		width: '1.2rem',
		height: '1.2rem',
		borderRadius: '50%',
		backgroundColor: purple[600]
	},
	classCardIcon: {
		fontSize: '1.2rem',
		overflow: 'unset',
		marginRight: '.1rem',
		color: purple[600]
	},
	privateCardDot: {
		backgroundColor: yellow[600]
	},
	fvrtCardDot: {
		backgroundColor: gray[700]
	},
	fvrtAlertIcon: { color: dangers[500] },
	classCardClassName: {
		fontSize: '2rem',
		fontWeight: 800
	},
	classCardDetailsLinkContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		flex: 1
	},
	contentContainer: {
		padding: '1rem',
		backgroundColor: 'white',
		display: 'flex',
		flexDirection: 'column',
		gap: '.5rem',

		// lots of calendar customizations
		'& .rbc-calendar': {
			'& .rbc-time-view': {
				backgroundColor: 'white',
				'& .rbc-current-time-indicator': {
					backgroundColor: red[600],
					height: '2px',
					'&:before': {
						content:
							"url(\"data:image/svg+xml,<svg width='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'><circle cx='4' cy='4' r='4' /></svg>\")",
						fill: red[600],
						position: 'relative',
						top: '-.75rem'
					}
				},
				'& .rbc-event': {
					border: 'none',
					backgroundColor: 'unset',
					// left: 'calc(0% - 1px) !important',
					paddingLeft: 0,
					zIndex: 1200
				},
				'& .rbc-time-header': {
					height: 'auto',
					'& .rbc-header': {
						borderTop: `0.0625rem solid ${gray[500]}`,
						borderBottom: 'none',
						padding: '.8rem .3rem',
						borderColor: gray[500]
					}
				},
				'& .rbc-time-header-gutter': {
					width: '5.25rem !important',
					minWidth: '5.25rem !important',
					maxWidth: '5.25rem !important'
				},
				'& .rbc-timeslot-group': {
					backgroundColor: 'unset',
					border: `0.0625rem solid ${gray[500]}`,
					borderLeft: 'none',
					borderBottom: 'none',
					minHeight: '78px'
				},
				'& .rbc-time-column': {
					border: 'none',
					borderRadius: '0'
				},
				'& .rbc-time-content ': {
					borderBottom: `0.0625rem solid ${gray[500]}`
				},
				'& .rbc-time-gutter': {
					opacity: 'unset',
					'& > .rbc-timeslot-group': {
						borderBottom: 'none',
						borderTop: `0.0625rem solid ${gray[500]}`,
						'& > .rbc-time-slot': {
							display: 'flex',
							alignItems: 'flex-start',
							justifyContent: 'flex-end',
							'& .rbc-label': {
								fontSize: '0.875rem',
								fontFamily: 'Nunito',
								fontWeight: 600
							}
						}
					}
				},
				'& .rbc-allday-cell': {
					display: 'none'
				}
			},
			'& .rbc-month-view': {
				backgroundColor: 'white',
				borderRadius: '0.375rem',
				borderColor: gray[500],
				'& .rbc-month-row': {
					overflow: 'unset'
				},
				'& .rbc-show-more': {
					paddingLeft: '0.875rem',
					color: purple[600],
					fontFamily: 'Nunito',
					fontWeight: 800,
					fontSize: '0.875rem'
				},
				'& .rbc-month-header': {
					'& .rbc-header': {
						padding: '.8rem .3rem',
						borderColor: gray[500]
					}
				},
				'& .rbc-off-range-bg': {
					backgroundColor: gray[200]
				},
				'& .rbc-off-range': {
					'& > div': {
						display: 'none'
					}
				}
			},
			'& .rbc-today': {
				backgroundColor: 'white'
			}
		}
	},
	calendarWeekHeader: {
		width: '50px',
		height: '50px',
		borderRadius: '50%',
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: 'white',
		color: gray[900],
		justifyContent: 'center',
		alignItems: 'center',
		'& .day': {
			fontSize: '.75rem',
			fontFamily: 'Nunito',
			fontWeight: 800
		},
		'& .day-of-month': {
			fontSize: '1rem',
			fontFamily: 'Nunito',
			fontWeight: 800
		}
	},
	calendarWeekHeaderSelected: {
		backgroundColor: purple[600],
		color: 'white'
	},
	calendarWeekHeaderToday: {
		backgroundColor: gray[500]
	},
	calendarMonthHeader: {
		display: 'flex',
		alignItems: 'center',
		height: '3rem',
		fontSize: '1rem',
		textTransform: 'uppercase',
		fontWeight: 800,
		color: gray[900],
		fontFamily: 'Nunito',
		paddingLeft: '4px'
	},
	calendarMonthDay: {
		display: 'flex',
		marginBottom: '.5rem',
		paddingTop: '.4rem',
		paddingLeft: '.4rem',
		'& > span': {
			width: '1.5rem',
			height: '1.5rem',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			borderRadius: '50%',
			fontSize: '0.875rem',
			fontFamily: 'Nunito',
			fontWeight: 800,
			color: gray[900]
		}
	},
	calendarMonthDaySelected: {
		'& > span': {
			backgroundColor: purple[600],
			color: 'white'
		}
	},
	calendarMonthDayToday: {
		'& > span': {
			backgroundColor: gray[500]
		}
	},
	calendarToolbarContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		padding: '1rem',
		border: `0.0625rem solid ${gray[500]}`,
		borderRadius: '0.375rem',
		marginBottom: '.5rem',
		'& .label': {
			fontSize: '1rem',
			fontWeight: 800,
			display: 'flex',
			alignItems: 'center'
		}
	},
	monthSelect: {
		width: '9.375rem',
		padding: '.3rem',
		border: `0.0625rem solid ${gray[300]}`,
		borderRadius: '4px',
		fontFamily: 'Nunito',
		background: gray[300],
		color: gray[800]
	},
	rowWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		paddingLeft: '2rem',
		marginTop: '1rem'
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		'& > div': {
			flex: 1
		},
		gap: '1rem'
	},
	rowColumn: {
		display: 'flex',
		alignContent: 'center',
		alignItems: 'center',
		'& > div': {
			flex: 1
		}
	},
	assignButtonContainer: {
		display: 'flex',
		alignItems: 'center',
		paddingTop: '1.8rem'
	},
	loadingContainer: {
		position: 'absolute',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1000,
		flex: 1,
		width: '100%',
		height: '100%',
		background: 'rgba(255, 255, 255, .5)'
	}
}))

const WeekHeader = (props) => {
	const classes = useStyles()
	const { date: propsDate, selectedDate } = props

	if (!propsDate) {
		return null
	}

	const momentDate = moment(propsDate)
	const normalizedDate = momentDate.format('YYYY-MM-DD')
	const dayOfWeek = momentDate.format('ddd')
	const dayOfMonth = momentDate.format('D')
	const selected = selectedDate === normalizedDate
	const today = normalizedDate === moment().format('YYYY-MM-DD')
	const className = clsx([
		classes.calendarWeekHeader,
		today && !selected ? classes.calendarWeekHeaderToday : null,
		selected ? classes.calendarWeekHeaderSelected : null
	])

	return (
		<div className={className}>
			<span className="day">{dayOfWeek}</span>
			<span className="day-of-month">{dayOfMonth}</span>
		</div>
	)
}

const MonthDateHeader = (props) => {
	const classes = useStyles()
	const { date: propsDate, label, selectedDate } = props
	const momentDate = moment(propsDate)
	const normalizedDate = momentDate.format('YYYY-MM-DD')

	const selected = selectedDate === normalizedDate
	const today = normalizedDate === moment().format('YYYY-MM-DD')
	const className = clsx([
		classes.calendarMonthDay,
		today && !selected ? classes.calendarMonthDayToday : null,
		selected ? classes.calendarMonthDaySelected : null
	])
	return (
		<div className={className}>
			<span>{label}</span>
		</div>
	)
}

const MonthViewEvent = (props) => {
	const classes = useStyles()
	const { event, onClick, onDoubleClick, onKeyPress, onSelect, selected = false } = props

	if (!event) {
		return null
	}

	const { title, classData } = event

	const useLock = classData?.access === ADMIN_ONLY
	const isFVRTSession = classData?.type === FACILITY_VISIT_RELATED_TRAINING
	const isPrivate = classData?.training_course_id?.categories?.includes('private') || false

	let showAlert = false

	if (isFVRTSession && (!classData?.instructor_id || !classData?.time_scheduled)) {
		showAlert = true
	}

	let tooltipTitle = title
	if (isFVRTSession) {
		tooltipTitle = (
			<div>
				<Typography
					style={{
						...getChipStyles(classData?.visit.status?.name?.toLowerCase()),
						background: 'transparent',
						backgroundColor: 'transparent',
						fontSize: 12,
						fontWeight: '600',
						textTransform: 'unset'
					}}>
					{`\u2022`} {classData?.visit?.status?.name}
				</Typography>
				Pet: {classData?.pet?.name}
				<br />
				Client: {classData?.pet?.household?.accounts?.find((account) => account.primary)?.full_name}
			</div>
		)
	}

	const _containerClasses = [classes.dayViewEvent]
	const _iconClasses = [classes.dayViewEventDot]

	if (selected) {
		_containerClasses.push(classes.eventSelected)
	}

	if (isFVRTSession) {
		_containerClasses.push(classes.dayViewEventFVRT)
		_iconClasses.push(classes.dayViewEventDotFVRT)
		if (selected) {
			_containerClasses.push(classes.eventSelectedFVRT)
		}
	}

	if (isPrivate) {
		_containerClasses.push(classes.dayViewEventPrivate)
		_iconClasses.push(classes.dayViewEventDotPrivate)
		if (selected) {
			_containerClasses.push(classes.eventSelectedPrivate)
		}
	}

	const containerClasses = clsx(_containerClasses)
	const iconClasses = clsx(_iconClasses)

	return (
		<div
			className="rbc-event"
			onClick={onClick}
			onDoubleClick={onDoubleClick}
			onKeyPress={onKeyPress}
			onSelect={onSelect}
			selected={selected}
			style={{
				...props.children.props.style,
				height: 'auto',
				display: 'flex',
				backgroundColor: 'unset',
				color: 'unset'
			}}>
			<Tooltip title={tooltipTitle}>
				<div className={classes.dayViewEventContainer}>
					<div className={containerClasses}>
						<div className={classes.dayViewEventInnerContainer}>
							<div className={classes.dayViewEventTextContainer}>
								{useLock ? (
									<span>
										<Icon className={`fas fa-lock ${classes.dayEventLock}`} />
									</span>
								) : null}
								{showAlert ? (
									<span style={{ color: red[600] }}>
										<Icon
											className={`fas fa-calendar-exclamation ${classes.dayEventIssues}`}
										/>
									</span>
								) : null}
								{!useLock && !showAlert ? <span className={iconClasses}></span> : null}
								<span>{title}</span>
							</div>
						</div>
					</div>
				</div>
			</Tooltip>
		</div>
	)
}

const ClassDetailsView = ({ classDetails, facility }) => {
	if (!classDetails) {
		return null
	}
	const classes = useStyles()
	return (
		<div className={classes.classCard}>
			<div className={classes.classCardNameContainer}>
				{classDetails.access === ADMIN_ONLY ? (
					<div>
						<Icon className={`fas fa-lock ${classes.classCardIcon}`} />
					</div>
				) : (
					<div className={classes.classCardDot} />
				)}
				<span className={classes.classCardClassName}>
					{classDetails.training_course_id?.name} (W
					{classDetails.sessionNumber})
				</span>
				<div className={classes.classCardDetailsLinkContainer}>
					<Link
						component={RouterLink}
						underline="none"
						onClick={(e) => e.stopPropagation()}
						to={`/facilities/${facility._id}/training/class/${classDetails._id}?date=${classDetails.formattedDate}`}>
						<span>View Class Details</span>
					</Link>
				</div>
			</div>
			<div className={classes.classCardDetailsContainer}>
				{moment(classDetails.formattedDate).format('dddd, MMMM DD, YYYY')},{' '}
				{convert24HourTimeTo12HourTime(classDetails.start_time)} -{' '}
				{convert24HourTimeTo12HourTime(classDetails.end_time)}
			</div>
			<div className={classes.classCardDetailsContainer}>
				Attendees - {classDetails.enrollment_count || 0} /{' '}
				{classDetails.capacity_max || classDetails.training_course_id?.capacity_max} Enrolled
			</div>
			<div className={classes.classCardDetailsContainer}>
				Instructor - {classDetails.instructor_id?.full_name}
			</div>
		</div>
	)
}

const FVRTDetailsView = ({ classDetails }) => {
	if (!classDetails) {
		return null
	}
	const classes = useStyles()

	const [editedDetails, setEditedDetails] = useState({ staff_id: '', time_scheduled: '' })
	const [editedKeys, setEditedKeys] = useState(new Set())
	const [instructor, setInstructor] = useState(classDetails?.instructor_id || null)
	const [timeScheduled, setTimeScheduled] = useState(classDetails?.time_scheduled || null)
	const [busy, setBusy] = useState(false)

	const { trainingStaff, updateFVRTSession } = useSelectedFacility()

	useEffect(() => {
		setEditedDetails((prev) => ({
			...prev,
			staff_id: classDetails?.instructor_id?._id || '',
			time_scheduled: classDetails?.time_scheduled || ''
		}))

		setInstructor(classDetails?.instructor_id || null)
		setTimeScheduled(classDetails?.time_scheduled || null)
	}, [classDetails])

	const handleFieldUpdate = ({ field, targetValue, unset = false }) => {
		setEditedDetails((prev) => {
			const revised = { ...prev, [field]: targetValue }
			return revised
		})
		setEditedKeys((keys) => {
			keys.add(field)
			return new Set(keys)
		})
	}

	let showAlert = false

	const { name, pet, visit, _id: addon_id } = classDetails
	const primaryAccount = pet?.household?.accounts?.find((account) => account.primary)
	const household = pet?.household || null

	if (!instructor || !timeScheduled) {
		showAlert = true
	}

	const disableEditing = [STATUS_ID.Cancelled, STATUS_ID['No Show']].includes(visit?.status?.id)

	const canAssign = editedKeys.size > 0

	const handleOnSave = async () => {
		setBusy(true)

		try {
			const updates = {}
			for (const key of editedKeys) {
				updates[key] = editedDetails[key]
			}
			const { success, data } = await updateFVRTSession({ addon_id, updates })
			if (success) {
				if (data) {
					const { instructor_id, time_scheduled } = data
					if (instructor_id || instructor_id === null) {
						setInstructor(instructor_id)
					}
					if (time_scheduled && time_scheduled > '') {
						setTimeScheduled(time_scheduled)
					}
				}
				setEditedKeys(new Set())
			}
		} catch (e) {
			console.error(e)
		} finally {
			setBusy(false)
		}
	}

	return (
		<div className={classes.classCard} style={{ position: 'relative' }}>
			{busy ? (
				<div className={classes.loadingContainer}>
					<CircularProgress variant="indeterminate" size="3rem" />
				</div>
			) : null}
			<div className={classes.classCardNameContainer}>
				{showAlert ? (
					<div>
						<Icon
							className={`fas fa-calendar-exclamation ${classes.classCardIcon} ${classes.fvrtAlertIcon}`}
						/>
					</div>
				) : (
					<div className={clsx([classes.classCardDot, classes.fvrtCardDot])} />
				)}
				<span className={classes.classCardClassName}>{name}</span>
				<div className={classes.classCardDetailsLinkContainer}>
					{household && visit ? (
						<Link
							component={RouterLink}
							underline="none"
							onClick={(e) => e.stopPropagation()}
							to={`/clients/${household._id}/appointments/${visit._id}`}>
							<span>View Appointment Details</span>
						</Link>
					) : null}
				</div>
			</div>
			<div className={classes.fvrtCardDetailsOuterContainer}>
				<div className={classes.fvrtCardDetailsInnerContainer}>
					<div className={classes.classCardDetailsContainer}>
						{moment(classDetails.formattedDate).format('dddd, MMMM DD, YYYY')}
					</div>
					<div className={classes.classCardDetailsContainer}>
						Instructor - {instructor ? instructor.full_name : '[ASSIGNMENT NEEDED]'}
					</div>
				</div>
				<div className={classes.fvrtCardDetailsInnerContainer}>
					<div className={classes.classCardDetailsContainer}>
						<Typography variant="overline" style={{ lineHeight: 'unset' }}>
							Household Info
						</Typography>
					</div>
					<div className={classes.classCardDetailsContainer}>
						Pet:{' '}
						{pet && household ? (
							<Link
								component={RouterLink}
								underline="none"
								onClick={(e) => e.stopPropagation()}
								to={`/clients/${household._id}/pets/${pet._id}`}>
								<span>{pet.name}</span>
							</Link>
						) : (
							'Pet missing'
						)}
					</div>
					<div className={classes.classCardDetailsContainer}>
						Client: {primaryAccount?.first_name || 'First'} {primaryAccount?.last_name || 'Last'}
					</div>
				</div>
				<div className={classes.fvrtCardDetailsInnerContainer}>
					<div className={classes.classCardDetailsContainer}>
						<Typography variant="overline" style={{ lineHeight: 'unset' }}>
							Appointment Info
						</Typography>
					</div>
					<div className={classes.classCardDetailsContainer}>
						<Typography
							style={{
								...getChipStyles(visit.status?.name?.toLowerCase()),
								background: 'transparent',
								backgroundColor: 'transparent',
								fontSize: 14,
								fontWeight: '600',
								textTransform: 'unset'
							}}>
							{`\u2022`} {visit?.status?.name}
						</Typography>
					</div>
				</div>
			</div>
			<div className={classes.rowWrapper}>
				<div className={classes.row}>
					<div className={classes.rowColumn}>
						<StyledTextField
							variant="filled"
							label="Instructor"
							header="Instructor"
							select
							value={editedDetails.staff_id}
							disabled={disableEditing}
							required={true}
							onChange={(e) => {
								handleFieldUpdate({
									field: 'staff_id',
									targetValue: e.target.value
								})
							}}>
							{trainingStaff.map(({ _id, name }) => {
								return (
									<MenuItem key={_id} value={_id}>
										{name}
									</MenuItem>
								)
							})}
						</StyledTextField>
						{!disableEditing ? (
							<IconButton
								disableFocusRipple
								disableRipple
								disabled={disableEditing}
								style={{
									backgroundColor: 'transparent',
									marginTop: '2.5rem'
								}}
								onClick={() => {
									handleFieldUpdate({
										field: 'staff_id',
										targetValue: null,
										unset: true
									})
									setInstructor(null)
								}}>
								<Icon className="fas fa-times" style={{ color: purple[600], fontSize: 16 }} />
							</IconButton>
						) : null}
					</div>

					<div className={classes.rowColumn}>
						<StyledKeyboardTimePicker
							disableFuture
							ampm={false}
							format="HH:mm"
							required
							label="Start Time"
							header="Start Time (24hr format)"
							initialvalue={editedDetails.time_scheduled}
							disabled={disableEditing}
							timeChanged={(e) => {
								const { formatted } = e
								handleFieldUpdate({
									field: 'time_scheduled',
									targetValue: formatted
								})
							}}
						/>
						{!disableEditing ? (
							<IconButton
								disableFocusRipple
								disableRipple
								disabled={disableEditing}
								style={{
									backgroundColor: 'transparent',
									marginTop: '2.5rem'
								}}
								onClick={() => {
									handleFieldUpdate({
										field: 'time_scheduled',
										targetValue: '',
										unset: true
									})
									setTimeScheduled(null)
								}}>
								<Icon className="fas fa-times" style={{ color: purple[600], fontSize: 16 }} />
							</IconButton>
						) : null}
					</div>
					<div className={classes.assignButtonContainer}>
						<MainButton
							label={'Assign'}
							variant="contained"
							disabled={!canAssign}
							onClick={handleOnSave}
						/>
					</div>
				</div>
			</div>
		</div>
	)
}

const TimeViewEvent = (props) => {
	const classes = useStyles()
	const { event, onClick, onDoubleClick, onKeyPress, selected = false, label } = props

	if (!event) {
		return null
	}

	const { title, classData, instructor_id } = event

	const useLock = classData?.access === ADMIN_ONLY
	const isFVRTSession = classData?.type === FACILITY_VISIT_RELATED_TRAINING
	const isPrivate = classData?.training_course_id?.categories?.includes('private') || false

	let showAlert = false
	let displayLabel = label

	if (isFVRTSession && (!classData?.instructor_id || !classData?.time_scheduled)) {
		showAlert = true
	}

	if (isFVRTSession && !classData?.time_scheduled) {
		displayLabel = `[NO TIME SCHEDULED]`
	}

	const _containerClasses = [classes.weekViewEvent]
	const _iconClasses = [classes.weekViewEventDot]

	if (selected) {
		_containerClasses.push(classes.eventSelected)
	}

	if (isFVRTSession) {
		_containerClasses.push(classes.weekViewEventFVRT)
		_iconClasses.push(classes.weekViewEventDotFVRT)
		if (selected) {
			_containerClasses.push(classes.eventSelectedFVRT)
		}
	}

	if (isPrivate) {
		_containerClasses.push(classes.weekViewEventPrivate)
		_iconClasses.push(classes.weekViewEventDotPrivate)
		if (selected) {
			_containerClasses.push(classes.eventSelectedPrivate)
		}
	}

	const containerClasses = clsx(_containerClasses)
	const iconClasses = clsx(_iconClasses)

	return (
		<div
			className="rbc-event"
			onClick={onClick}
			onDoubleClick={onDoubleClick}
			onKeyPress={onKeyPress}
			selected={selected}
			style={{ ...props.children.props.style, height: 'auto' }}>
			<div className={containerClasses}>
				{useLock ? (
					<span>
						<Icon className={`fas fa-lock ${classes.weekViewEventLock}`} />
					</span>
				) : null}
				{showAlert ? (
					<span style={{ color: red[600] }}>
						<Icon className={`fas fa-calendar-exclamation ${classes.weekViewEventIssues}`} />
					</span>
				) : null}
				{!useLock && !showAlert ? <span className={iconClasses}></span> : null}
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<div>{title}</div>
					<div>{displayLabel}</div>
					<div>{instructor_id ? instructor_id.full_name : classData.instructor_id?.full_name || ''}</div>
				</div>
			</div>
		</div>
	)
}

const MonthHeader = (props) => {
	const classes = useStyles()
	const { label } = props
	return <div className={classes.calendarMonthHeader}>{label}</div>
}

const CalendarToolbar = (props) => {
	const classes = useStyles()
	const { label, onView, views, view, onViewChanged } = props

	const handleSelectChange = (value) => {
		onView(value)
		if (onViewChanged) {
			onViewChanged(value)
		}
	}
	return (
		<div className={classes.calendarToolbarContainer}>
			<div className="label">{label}</div>
			<div>
				<StyledSimpleSelect
					value={view}
					options={views.map((view) => ({ value: view, label: VIEW_LABELS[view] }))}
					onValueChanged={handleSelectChange}
				/>
			</div>
		</div>
	)
}

const TrainingCalendar = () => {
	const classes = useStyles()
	const theme = useTheme()

	const { facility } = useSelectedFacility()
	const { availableContentHeight } = useDomDimensions()
	const { pushQueryParamToHistory, getQueryParam, replaceQueryParamInHistory } = useQuery()

	const [selectedClass, setSelectedClass] = useState(null)
	const [editDialogOpen, setEditDialogOpen] = useState(false)
	const [events, setEvents] = useState([])
	const [currentRequestedTimeFrame, setCurrentRequestedTimeFrame] = useState([])

	const calendarRef = useRef(null)

	const queryDate = getQueryParam('date')
	const viewParam = getQueryParam('view')

	const [date, setDate] = useState()
	const [calendarDate, setCalendarDate] = useState()
	const [view, setView] = useState('month')

	const {
		loading,
		trainingCourses,
		trainingLocations,
		trainingCalendar,
		getTrainingCoursesForFacility,
		getTrainingLocationsForFacility,
		getTrainingClassCalendarForMonthAndYearForFacility
	} = useSelectedFacility()

	useEffect(() => {
		if (queryDate) {
			setDate(queryDate)
			setCalendarDate(moment(queryDate).toDate())
		} else {
			const today = moment()
			const todayFormatted = today.format('YYYY-MM-DD')
			setDate(todayFormatted)
			setCalendarDate(today.toDate())
			replaceQueryParamInHistory({ key: 'date', value: todayFormatted })
		}
	}, [queryDate])

	useEffect(() => {
		if (viewParam) {
			setView(viewParam)
		} else {
			setView('month')
			replaceQueryParamInHistory({ key: 'view', value: 'month' })
		}
	}, [viewParam])

	const getClasses = () => {
		const requestedDate = moment(date)
		getTrainingClassCalendarForMonthAndYearForFacility({
			month: requestedDate.format('MM'),
			year: requestedDate.format('YYYY')
		})
		setCurrentRequestedTimeFrame([
			requestedDate.format('MM-YYYY'),
			requestedDate.clone().add(1, 'month').format('MM-YYYY'),
			requestedDate.clone().subtract(1, 'month').format('MM-YYYY')
		])
	}

	const handleEditDialogOpen = () => {
		setEditDialogOpen(true)
	}

	const handleEditDialogClose = () => {
		setEditDialogOpen(false)
	}

	const handleNewClassSaved = () => {
		setEditDialogOpen(false)
		getClasses()
	}

	useEffect(() => {
		getClasses()
	}, [facility])

	useEffect(() => {
		const requestedDate = moment(date)
		const checkDate = requestedDate.format('MM-YYYY')
		if (!currentRequestedTimeFrame.includes(checkDate)) {
			getClasses()
		}
	}, [currentRequestedTimeFrame, date])

	useEffect(() => {
		if (!trainingCourses || trainingCourses.length === 0) {
			getTrainingCoursesForFacility()
		}
		if (!trainingLocations || trainingLocations.length === 0) {
			getTrainingLocationsForFacility()
		}
	}, [getTrainingCoursesForFacility, getTrainingLocationsForFacility])

	useEffect(() => {
		if (trainingCalendar) {
			const events = []
			for (const [date, classes] of Object.entries(trainingCalendar)) {
				for (const trainingClass of classes) {
					const { type } = trainingClass
					if (type === FACILITY_VISIT_RELATED_TRAINING) {
						const timezone = trainingClass.facility_id?.market_id?.time_zone || 'America/New_York'
						const momentDate = moment(date).tz(timezone)

						const start_time = trainingClass?.time_scheduled || '09:00'
						const [startHour, startMinute] = start_time.split(':') || [0, 0]

						const start = momentDate.clone().set({ hour: startHour, minute: startMinute }).toDate()
						const end = momentDate
							.clone()
							.set({ hour: startHour, minute: startMinute })
							.add(30, 'minutes')
							.toDate()
						const title = trainingClass.name || 'Play & Study'
						let instructor_id = trainingClass.instructor_id
						events.push({
							title,
							formattedDate: date,
							timezone,
							start,
							end,
							allDay: false,
							classData: trainingClass,
							instructor_id
						})
					}
					if (type === TRAINING_CLASS) {
						const timezone =
							trainingClass.training_course_id?.facility_id?.market_id?.timezone ||
							'America/New_York'
						const momentDate = moment(date).tz(timezone)

						const [startHour, startMinute] = trainingClass.start_time?.split(':') || [0, 0]
						const [endHour, endMinute] = trainingClass.end_time.split(':') || [0, 0]

						const start = momentDate.set({ hour: startHour, minute: startMinute }).toDate()
						const end = momentDate.set({ hour: endHour, minute: endMinute }).toDate()

						const sessionNumber = trainingClass.dates.indexOf(date) + 1

						const title = `${trainingClass.training_course_id?.name} (W${sessionNumber})`

						let instructor_id = trainingClass.instructor_id
						if (trainingClass.adjustments && trainingClass.adjustments.length > 0) {
							const sessionAdjustment = trainingClass.adjustments.find(
								(adj) => adj.session === sessionNumber
							)
							if (sessionAdjustment) {
								if (sessionAdjustment.new_instructor) {
									instructor_id = sessionAdjustment.new_instructor
								}
							}
						}

						events.push({
							title,
							formattedDate: date,
							timezone,
							start,
							end,
							allDay: false,
							sessionNumber,
							classData: trainingClass,
							instructor_id
						})
					}
				}
			}
			setEvents(events)
		}
	}, [trainingCalendar])

	const handleDateChanged = (date) => {
		calendarRef.current?.props?.onNavigate(date)
		pushQueryParamToHistory({ key: 'date', value: date.format('YYYY-MM-DD') })
		setDate(date.format('YYYY-MM-DD'))
		setCalendarDate(date)
	}

	const contentHeight = useMemo(() => {
		let height = availableContentHeight - TOOLBAR_HEIGHT
		return height
	}, [availableContentHeight, selectedClass])

	const isFVRTSession = useMemo(() => {
		if (selectedClass) {
			return selectedClass.type === FACILITY_VISIT_RELATED_TRAINING
		}
		return false
	}, [selectedClass])

	const handleSelectEvent = (event) => {
		const { formattedDate, classData, sessionNumber, instructor_id } = event

		if (
			selectedClass &&
			selectedClass.formattedDate === formattedDate &&
			selectedClass._id === classData._id
		) {
			setSelectedClass(null)
		} else {
			// if formattedDate is same and classDate id is same, then deselect
			setSelectedClass({ ...classData, formattedDate, sessionNumber, instructor_id })
		}
	}

	const handleDeselectEvent = () => {
		setSelectedClass(null)
	}

	const handleOnViewChanged = (view) => {
		setSelectedClass(null)
		setView(view)
		replaceQueryParamInHistory({ key: 'view', value: view })
	}

	const { components } = useMemo(
		() => ({
			components: {
				toolbar: (props) => <CalendarToolbar {...props} onViewChanged={handleOnViewChanged} />,
				eventWrapper: (props) => {
					const { type, event } = props
					const { classData } = event
					if (type === 'date') {
						return (
							<MonthViewEvent
								{...props}
								selected={
									selectedClass?._id === classData._id &&
									selectedClass.formattedDate === event.formattedDate
								}
								onClick={() => handleSelectEvent(event)}
								onDoubleClick={handleDeselectEvent}
							/>
						)
					}
					if (type === 'time') {
						return (
							<TimeViewEvent
								{...props}
								selected={
									selectedClass?._id === classData._id &&
									selectedClass.formattedDate === event.formattedDate
								}
							/>
						)
					}
				},
				week: {
					header: (props) => <WeekHeader {...props} selectedDate={date} />
				},
				month: {
					header: (props) => <MonthHeader {...props} />,
					dateHeader: (props) => <MonthDateHeader {...props} selectedDate={date} />
				}
			}
		}),
		[date, selectedClass]
	)

	return (
		<div className={classes.root}>
			<TrainingClassDialog
				open={editDialogOpen}
				onClose={handleEditDialogClose}
				onSaved={handleNewClassSaved}
				onCancelled={handleEditDialogClose}
				title="ADD A CLASS"
				mode={DIALOG_EDIT_MODES.New}
				trainingClass={{ ...TRAINING_CLASS_TEMPLATE }}
			/>
			{facility ? (
				<div style={{ minHeight: `${availableContentHeight}px` }}>
					<Toolbar className={classes.toolbar}>
						<Typography variant="h6" color="primary" className={classes.toolbarHeader}>
							Training Calendar
						</Typography>
						<StyledToolbarDatePicker date={date} onDateChanged={handleDateChanged} />
						<MainButton
							label="New Class"
							onClick={handleEditDialogOpen}
							disabled={loading || trainingCourses.length === 0}
							startIcon={
								<AddOutlined
									style={{
										fill:
											loading || trainingCourses.length === 0
												? 'gray'
												: theme.palette.secondary.main
									}}
								/>
							}
						/>
					</Toolbar>
					<div
						className={classes.contentContainer}
						style={{
							height: `${contentHeight}px`
						}}>
						{selectedClass ? (
							isFVRTSession ? (
								<FVRTDetailsView classDetails={selectedClass} />
							) : (
								<ClassDetailsView classDetails={selectedClass} facility={facility} />
							)
						) : null}
						<MuiThemeProvider theme={table}>
							<Calendar
								ref={calendarRef}
								dayLayoutAlgorithm={'no-overlap'}
								components={components}
								events={events}
								localizer={localizer}
								date={calendarDate}
								// showMultiDayTimes
								step={60}
								views={['month', 'week']}
								view={view}
								toolbar={true}
								min={new Date(0, 0, 0, 9, 0, 0)}
								max={new Date(0, 0, 0, 22, 0, 0)}
								onSelectEvent={(event) => {
									handleSelectEvent(event)
								}}
								// style={{ minHeight: '1000px' }}
								timeslots={1}
								popup
							/>
						</MuiThemeProvider>
					</div>
				</div>
			) : (
				<h3>Please Select a Facility</h3>
			)}
		</div>
	)
}

export default TrainingCalendar
